import React, { memo, useMemo } from 'react';
import { QualityTypes } from '@cloudinary/url-gen/types/types';
import styled from '@emotion/styled';
import { borderRadius } from '@naf/theme';
import { useCloudinary } from '../../hooks/Cloudinary/useCloudinary';
import { buildImageSources } from './ResponsiveImage.utils';

type Props = {
  altText: string;
  /** CSS width this image occupies. Required: only the call site knows it — see `IMAGE_SIZES`. */
  sizes: string;
  quality?: QualityTypes;
  className?: string;
  imageId?: string;
  aspectRatio?: string;
  /**
   * LCP image only — fetched eagerly at high priority. Everything else is `loading="lazy"`, which
   * also suppresses React SSR's per-`<img>` preload link.
   */
  priority?: boolean;
};

const ResponsiveImage = memo(
  ({ imageId, quality, altText, className, aspectRatio, sizes, priority = false }: Props) => {
    // `getProcessedImage`, not `getResponsiveImage`: the latter bakes in `dpr_N`, which a
    // `w`-descriptor srcset must not — the browser applies the device ratio itself.
    const { getProcessedImage } = useCloudinary();

    const sources = useMemo(
      () => buildImageSources((width) => getProcessedImage(imageId, { width, quality, aspectRatio })?.toURL()),
      [getProcessedImage, imageId, quality, aspectRatio],
    );

    if (!sources) return null;

    return (
      <StyledImage
        src={sources.src}
        srcSet={sources.srcSet}
        sizes={sizes}
        alt={altText}
        className={className}
        loading={priority ? undefined : 'lazy'}
        fetchPriority={priority ? 'high' : undefined}
      />
    );
  },
);

export default ResponsiveImage;

const StyledImage = styled.img`
  width: 100%;
  border-radius: ${borderRadius.S};
`;
